import { useHashParams } from '@fondationcss/ui';
import { useContextToolbarStore } from '@/stores/context-toolbar-store';
import { cascadeHashParams, type ContextAxis } from './context-axis';

/**
 * Choosing a context item, exactly as if the user had done it in the selection bar.
 *
 * #723 asks that a click on a landing page's breakdown row "position the corresponding
 * dropdown […] as if it had been selected by hand". "As if by hand" is more demanding than it
 * looks: `context-toolbar.vue`'s handlers did not only write to the store, they also wrote the
 * fragment parameters, clearing the downstream ones. Hence this composable: the bar and the
 * landing pages now go through the same path, instead of two paths that look alike.
 *
 * The cascade itself lives in `context-axis.ts`, where it is testable — see the explanation at
 * the top of that file.
 */
export type { ContextAxis };

export function useContextSelection() {
  const store = useContextToolbarStore();
  const hashParams = useHashParams();

  /**
   * Whether this axis accepts a selection at all right now.
   *
   * Only the teacher axis answers no, and only for a scoped Enseignant: #879 locks their filter
   * on themselves — the bar forces the value and disables the unpin — because the server bounds
   * them to their own frequentations whatever the dropdown says. The landing pages went around
   * that lock (#1041): a breakdown row named a colleague, the click took it, and the bar then
   * displayed a pin the user could no longer remove over data that had never stopped being their
   * own.
   */
  function isSelectable(axis: ContextAxis): boolean {
    return !(axis === 'teacher' && store.teacherFilterLockedToSelf);
  }

  /**
   * Applies a selection by identifier. `null` clears the axis (and its cascade).
   *
   * Each axis looks in the list the corresponding menu displays — so the *filtered* lists, not
   * the raw ones: an identifier the bar does not offer must not become selectable through a
   * detour. A locked axis is the same rule one step further: the bar offers no choice there, so
   * neither does any other path into this function.
   */
  async function select(axis: ContextAxis, id: string | null): Promise<void> {
    if (!isSelectable(axis)) return;
    switch (axis) {
      case 'site':
        store.selectedSite = id ? store.sites.find(s => s.id === id) ?? null : null;
        break;
      case 'program':
        store.selectedProgram = id ? store.filteredPrograms.find(p => p.id === id) ?? null : null;
        break;
      case 'group':
        store.selectedGroup = id ? store.filteredGroups.find(g => g.id === id) ?? null : null;
        break;
      case 'teacher':
        store.selectedTeacher = id ? store.filteredTeachers.find(t => t.id === id) ?? null : null;
        break;
    }
    await hashParams.setMany(cascadeHashParams(axis, id ?? undefined));
  }

  /** Does the axis carry a selection? What `ContextLanding` queries to know whether it should
   *  show the detailed view or the summary. */
  function isSelected(axis: ContextAxis): boolean {
    switch (axis) {
      case 'site': return !!store.selectedSite;
      case 'program': return !!store.selectedProgram;
      case 'group': return !!store.selectedGroup;
      case 'teacher': return !!store.selectedTeacher;
    }
  }

  return { select, isSelected, isSelectable };
}
